import { useEffect, useState } from 'react';
import { FormControl, FormHelperText, Grid, InputLabel, OutlinedInput } from '@material-ui/core';
import { IUserProfile } from './IUserProfile.types';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import { AuthUser } from './AuthUser.component';
import { useStyles } from './EditUserEmail.styles';
import { validateFormatEmailInput } from '../../services/validation/validationUser';
import { verifyDuplicateMail } from '../../services/validation/validation.service';
import { useLayoutContext } from '../../Context/Layout.context';

interface EditUserEmailProps {
  user?: IUserProfile;
  userId: string;
  open: boolean;
  onClose: () => void;
  onNavBack: () => void;
}

export const EditUserEmail: React.FC<EditUserEmailProps> = (props) => {
  const classes = useStyles();
  const { openAlert } = useLayoutContext();

  const [newEmail, setNewEmail] = useState<string>('');
  const [repeatedEmail, setRepeatedEmail] = useState<string>('');
  const [validNewEmail, setValidNewEmail] = useState<boolean>(true);
  const [emailsMatch, setEmailsMatch] = useState<boolean>(true);
  const [uniqueEmail, setUniqueEmail] = useState<boolean>(true);
  const [newEmailErrorMessage, setNewEmailErrorMessage] = useState<string>('');
  const [repeatedEmailErrorMessage, setRepeatedEmailErrorMessage] = useState<string>('');
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true);
  const [newInput, setIsNewInput] = useState<boolean>(false);
  const [allInputsFilled, setAllInputsFilled] = useState<boolean>(false);
  const [openAuthUser, setOpenAuthUser] = useState(false);

  const clearInputs = () => {
    if (props.user) {
      setNewEmail(props.user.email);
    }
    setRepeatedEmail('');
    setValidNewEmail(true);
    setUniqueEmail(true);
    setEmailsMatch(true);
    setNewEmailErrorMessage('');
    setRepeatedEmailErrorMessage('');
    setIsUpdateButtonDisabled(true);
  };

  const handleChangeNewEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmail(event.target.value);
    setIsNewInput(allInputsFilled);
  };

  const handleChangeRepeatedEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeatedEmail(event.target.value);
    setIsNewInput(allInputsFilled);
  };

  const handleOpenAuthUser = () => {
    setOpenAuthUser(true);
  };

  const handleAuthUserClose = () => {
    setOpenAuthUser(false);
  };

  const getEmailsMatch = () => {
    setEmailsMatch(newEmail === repeatedEmail);
    if (newEmail !== repeatedEmail) {
      setRepeatedEmailErrorMessage('Podane adresy e-mail nie są takie same');
    } else setRepeatedEmailErrorMessage('');
  };

  const handleVerifyDuplicateMail = () => {
    verifyDuplicateMail(newEmail).then(
      (res: boolean) => {
        setUniqueEmail(res);
        if (!res) {
          setRepeatedEmailErrorMessage('Podany nowy adres e-mail istnieje już w systemie');
        } else {
          getEmailsMatch();
          if (newEmail === repeatedEmail) {
            handleOpenAuthUser();
          }
        }
      },
      () => {
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
        } else openAlert('error');
        props.onClose()
      },
    );
  };

  const handleVerifyEmailInput = (email: string) => {
    const hasValidEmailFormat = validateFormatEmailInput(email);
    if (hasValidEmailFormat) {
      handleVerifyDuplicateMail();
    } else {
      setValidNewEmail(false);
      setNewEmailErrorMessage('Podany e-mail jest nieprawidłowy');
    }
  };

  useEffect(() => {
    if (props.user) {
      setNewEmail(props.user.email);
    }
  }, [props.user]);

  useEffect(() => {
    if (newEmail !== '' || repeatedEmail !== '') {
      setValidNewEmail(true);
      setUniqueEmail(true);
      setEmailsMatch(true);
      setNewEmailErrorMessage('');
      setRepeatedEmailErrorMessage('');
      setIsUpdateButtonDisabled(true);
    } else {
      setAllInputsFilled(false);
      setIsUpdateButtonDisabled(true);
    }
  }, [newEmail, repeatedEmail]);

  useEffect(() => {
    if (newInput) {
      setIsUpdateButtonDisabled(false);
    } else if (newEmailErrorMessage !== '' || repeatedEmailErrorMessage !== '' || repeatedEmail === '') {
      setIsUpdateButtonDisabled(true);
    } else setIsUpdateButtonDisabled(false);
  }, [newInput, newEmailErrorMessage, repeatedEmailErrorMessage, newEmail, repeatedEmail]);

  const onCancelEdit = () => {
    clearInputs();
    props.onClose();
    props.onNavBack();
    handleAuthUserClose();
  };

  return (
    <>
      <ModalWithButtons
        open={props.open}
        size="sm"
        navButtonLabel="Mój profil"
        handleNavBack={onCancelEdit}
        xCloseButton={onCancelEdit}
        autoHeight={true}
        onCancel={onCancelEdit}
        onConfirm={() => handleVerifyEmailInput(newEmail)}
        confirmButtonLabel="Aktualizuj"
        confirmButtonDisabled={isUpdateButtonDisabled}
        buttonsContainerStyle={{ marginTop: '40px' }}
      >
        <Grid container className={classes.container}>
          <Grid>
            <div className={classes.modalSubTitle}>Edytuj adres e-mail</div>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: !emailsMatch || !uniqueEmail || !validNewEmail ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="new-email"
              >
                E-mail
              </InputLabel>
              <OutlinedInput
                id="new-email"
                value={newEmail}
                onChange={handleChangeNewEmail}
                fullWidth={true}
                error={!emailsMatch || !uniqueEmail || !validNewEmail}
                inputProps={{
                  style: {
                    width: '255px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: !emailsMatch || !uniqueEmail || !validNewEmail ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="E-mail"
              />
              <FormHelperText
                className={classes.errorMessagesContainer}
                error
                id="new-email-error"
                data-testid="new-email-error"
                component={'div'}
              >
                {newEmailErrorMessage && <div className={classes.errorMessage}>{newEmailErrorMessage}</div>}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: !emailsMatch || !uniqueEmail ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="repeated-email"
              >
                Powtórz e-mail
              </InputLabel>
              <OutlinedInput
                data-testid="email-error"
                id="repeated-email"
                value={repeatedEmail}
                onChange={handleChangeRepeatedEmail}
                fullWidth={true}
                error={!emailsMatch || !uniqueEmail}
                inputProps={{
                  style: {
                    width: '255px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: !emailsMatch || !uniqueEmail ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Powtórz e-mail"
              />
              <FormHelperText className={classes.errorMessagesContainer} error id="email-error" component={'div'}>
                {repeatedEmailErrorMessage && <div className={classes.errorMessage}> {repeatedEmailErrorMessage} </div>}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </ModalWithButtons>
      <AuthUser
        open={openAuthUser}
        userId={props.userId}
        email={newEmail}
        onClose={handleAuthUserClose}
        onNavBack={props.onNavBack}
        onEditUserMailClose={props.onClose}
        onCloseUpdate={onCancelEdit}
        onCloseXButton={handleAuthUserClose}
      />
    </>
  );
};
